/**
 * Brand Profile Component
 * Modern, interactive profile management - works within DashboardLayout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { Save, X, Edit3, User, Mail, Globe, Instagram, Phone, DollarSign, FileText, Camera, Tag, CheckCircle, Clock } from 'lucide-react';

function BrandProfile() {
  const navigate = useNavigate();
  const { brand, isAuthenticated, loading, authFetch } = useBrand();
  
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [formData, setFormData] = useState({
    contact_name: '',
    phone: '',
    website: '',
    instagram_handle: '',
    location: '',
    logo: '',
    description: '',
    categories: '',
    default_currency: 'INR'
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/brand-portal/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await authFetch('/api/brand/profile/');
        const data = await response.json();

        if (response.ok) {
          setProfile(data);
          setFormData({
            contact_name: data.contact_name || '',
            phone: data.phone || '',
            website: data.website || '',
            instagram_handle: data.instagram_handle || '',
            location: data.location || '',
            logo: data.logo || '',
            description: data.description || '',
            categories: data.categories || '',
            default_currency: data.default_currency || 'INR'
          });
        } else {
          setError(data.error || 'Failed to load profile');
        }
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };

    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated, authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid image type. Use JPEG, PNG, WebP, or GIF.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large. Maximum size is 5MB.');
      return;
    }
    
    setUploadingLogo(true);
    setError('');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await authFetch('/api/brand/upload-image/', {
        method: 'POST',
        body: formDataUpload
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        setFormData(prev => ({ ...prev, logo: data.url }));
        setSuccess('Logo uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to upload logo');
      }
    } catch (err) {
      setError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch('/api/brand/profile/', {
        method: 'PATCH',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        setProfile(prev => ({ ...prev, ...formData }));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      contact_name: profile.contact_name || '',
      phone: profile.phone || '',
      website: profile.website || '',
      instagram_handle: profile.instagram_handle || '',
      location: profile.location || '',
      logo: profile.logo || '',
      description: profile.description || '',
      categories: profile.categories || '',
      default_currency: profile.default_currency || 'INR'
    });
    setError('');
  };

  if (loading || loadingProfile) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading profile...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bp-header-row" style={styles.header}>
        <div>
          <div style={styles.label}>S E T T I N G S</div>
          <h1 style={styles.title}>Brand Profile</h1>
          <p style={styles.subtitle}>Manage your brand information and settings</p>
        </div>
        {!isEditing && (
          <button onClick={() => setIsEditing(true)} style={styles.editButton}>
            <Edit3 style={{ width: '16px', height: '16px' }} />
            <span>Edit Profile</span>
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div style={styles.errorAlert}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={styles.alertClose}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      {success && (
        <div style={styles.successAlert}>
          <span>{success}</span>
          <button onClick={() => setSuccess('')} style={styles.alertCloseSuccess}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      {/* Profile Header Card */}
      <div style={styles.profileCard}>
        <div className="bp-profile-header" style={styles.profileHeader}>
          <div style={styles.avatarContainer}>
            {formData.logo ? (
              <img src={formData.logo} alt={profile?.name} style={styles.avatar} />
            ) : (
              <div style={styles.avatarPlaceholder}>
                {profile?.name?.charAt(0).toUpperCase() || 'B'}
              </div>
            )}
            {isEditing && (
              <div style={styles.avatarOverlay}>
                <Camera style={{ width: '20px', height: '20px', color: '#ffffff' }} />
              </div>
            )}
          </div>
          <div style={styles.profileInfo}>
            <h2 style={styles.profileName}>{profile?.name}</h2>
            <p style={styles.profileEmail}>{profile?.email}</p>
            <div style={styles.profileMeta}>
              <span style={{
                ...styles.statusBadge,
                background: profile?.status === 'active' ? '#f0fdf4' : '#fafafa',
                color: profile?.status === 'active' ? '#16a34a' : '#71717a',
                borderColor: profile?.status === 'active' ? '#bbf7d0' : '#e5e5e5',
              }}>
                {profile?.status?.charAt(0).toUpperCase() + profile?.status?.slice(1) || 'Active'}
              </span>
              <span style={styles.metaText}>{profile?.product_count || 0} products</span>
              <span style={styles.metaDot}>•</span>
              <span style={styles.metaText}>
                Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.label}>D E T A I L S</div>
          <h3 style={styles.cardTitle}>Profile Information</h3>
        </div>

        <div className="bp-form-grid" style={styles.formGrid}>
          {/* Contact Person */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <User style={{ width: '14px', height: '14px' }} />
              Contact Person
            </label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="Your name"
              style={{
                ...styles.input,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
          </div>

          {/* Phone Number */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Phone style={{ width: '14px', height: '14px' }} />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="+91 XXXXX XXXXX"
              style={{
                ...styles.input,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
          </div>

          {/* Website */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Globe style={{ width: '14px', height: '14px' }} />
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="https://yourbrand.com"
              style={{
                ...styles.input,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
          </div>

          {/* Instagram Handle */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Instagram style={{ width: '14px', height: '14px' }} />
              Instagram Handle
            </label>
            <div style={styles.inputWithPrefix}>
              <span style={styles.inputPrefix}>@</span>
              <input
                type="text"
                name="instagram_handle"
                value={formData.instagram_handle}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="yourbrand"
                style={{
                  ...styles.input,
                  ...styles.inputWithPrefixField,
                  ...(isEditing ? {} : styles.inputDisabled),
                }}
              />
            </div>
          </div>

          {/* Location */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="e.g. Srinagar, Kashmir"
              style={{
                ...styles.input,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
          </div>

          {/* Default Currency */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <DollarSign style={{ width: '14px', height: '14px' }} />
              Default Currency
            </label>
            <select
              name="default_currency"
              value={formData.default_currency}
              onChange={handleChange}
              disabled={!isEditing}
              style={{
                ...styles.input,
                ...styles.select,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            >
              <option value="INR">₹ INR - Indian Rupee</option>
              <option value="USD">$ USD - US Dollar</option>
              <option value="EUR">€ EUR - Euro</option>
            </select>
          </div>

          {/* Logo Upload - Full Width */}
          <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
            <label style={styles.formLabel}>
              <Camera style={{ width: '14px', height: '14px' }} />
              Brand Logo
            </label>
            {isEditing ? (
              <>
                <div style={styles.uploadContainer}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleLogoFileSelect}
                    style={{ display: 'none' }}
                    id="logo-upload"
                    disabled={uploadingLogo}
                  />
                  <label htmlFor="logo-upload" style={{
                    ...styles.uploadButton,
                    cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                    opacity: uploadingLogo ? 0.6 : 1,
                  }}>
                    <Camera style={{ width: '16px', height: '16px' }} />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </label>
                  <span style={styles.uploadHint}>
                    or paste URL below • JPEG, PNG, WebP or GIF • Max 5MB
                  </span>
                </div>
                {formData.logo && (
                  <div style={styles.logoPreview}>
                    <img 
                      src={formData.logo} 
                      alt="Logo preview" 
                      style={styles.logoPreviewImg}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <input
                  type="text"
                  name="logo"
                  value={formData.logo}
                  onChange={handleChange}
                  placeholder="https://example.com/logo.jpg"
                  style={{
                    ...styles.input,
                    marginTop: '8px',
                  }}
                />
                <span style={styles.charCount}>
                  Logo URL (max 1000 characters)
                </span>
              </>
            ) : (
              formData.logo && (
                <div style={styles.logoPreview}>
                  <img src={formData.logo} alt="Logo preview" style={styles.logoPreviewImg} />
                </div>
              )
            )}
          </div>

          {/* Brand Description - Full Width */}
          <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
            <label style={styles.formLabel}>
              <FileText style={{ width: '14px', height: '14px' }} />
              Brand Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="Tell customers what makes your brand special..."
              rows="4"
              style={{
                ...styles.input,
                ...styles.textarea,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
            <span style={styles.charCount}>
              {formData.description.length} / 500 characters
            </span>
          </div>

          {/* Product Categories - Full Width */}
          <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
            <label style={styles.formLabel}>
              <Tag style={{ width: '14px', height: '14px' }} />
              Product Categories
            </label>
            <input
              type="text"
              name="categories"
              value={formData.categories}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="e.g. Menswear, Womenswear, Pashmina, Shawl"
              style={{
                ...styles.input,
                ...(isEditing ? {} : styles.inputDisabled),
              }}
            />
            <span style={{ ...styles.charCount, marginTop: '4px', display: 'block' }}>
              Common product types you sell (comma-separated). Shown on your public brand page.
            </span>
          </div>
        </div>

        {/* Actions */}
        {isEditing && (
          <div style={styles.formActions}>
            <button onClick={handleCancel} style={styles.cancelButton} disabled={isSaving}>
              <X style={{ width: '16px', height: '16px' }} />
              Cancel
            </button>
            <button onClick={handleSave} style={styles.saveButton} disabled={isSaving}>
              {isSaving ? (
                <>
                  <div style={styles.buttonSpinner} />
                  Saving...
                </>
              ) : (
                <>
                  <Save style={{ width: '16px', height: '16px' }} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Onboarding fee status (read-only) */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.label}>O N B O A R D I N G</div>
          <h3 style={styles.cardTitle}>Partnership Fee</h3>
        </div>
        <div style={styles.infoList}>
          <div style={{ ...styles.infoItem, borderBottom: 'none' }}>
            <div style={styles.infoLeft}>
              <div style={styles.infoIcon}>
                {profile?.onboarding_fee_paid ? (
                  <CheckCircle style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                ) : (
                  <Clock style={{ width: '16px', height: '16px', color: '#71717a' }} />
                )}
              </div>
              <div>
                <div style={styles.infoLabel}>Status</div>
                <div style={{
                  ...styles.infoValue,
                  color: profile?.onboarding_fee_paid ? '#16a34a' : '#71717a',
                  fontWeight: 600,
                }}>
                  {profile?.onboarding_fee_paid ? 'Paid' : 'Pending'}
                </div>
              </div>
            </div>
          </div>
          {profile?.onboarding_fee_paid && profile?.onboarding_fee_paid_at && (
            <div style={{ ...styles.infoItem, borderBottom: 'none' }}>
              <div style={styles.infoLeft}>
                <div style={styles.infoIcon}><Clock style={{ width: '16px', height: '16px', color: '#71717a' }} /></div>
                <div>
                  <div style={styles.infoLabel}>Paid on</div>
                  <div style={styles.infoValue}>
                    {new Date(profile.onboarding_fee_paid_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>
          )}
          {profile?.onboarding_fee_paid && profile?.onboarding_fee_amount && (
            <div style={{ ...styles.infoItem, borderBottom: 'none' }}>
              <div style={styles.infoLeft}>
                <div style={styles.infoIcon}><DollarSign style={{ width: '16px', height: '16px', color: '#71717a' }} /></div>
                <div>
                  <div style={styles.infoLabel}>Amount</div>
                  <div style={styles.infoValue}>{profile.onboarding_fee_amount}</div>
                </div>
              </div>
            </div>
          )}
          {profile?.onboarding_fee_paid && profile?.onboarding_fee_payment_method && (
            <div style={{ ...styles.infoItem, borderBottom: 'none' }}>
              <div style={styles.infoLeft}>
                <div>
                  <div style={styles.infoLabel}>Payment method</div>
                  <div style={styles.infoValue}>{profile.onboarding_fee_payment_method}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Read-only Account Info */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.label}>A C C O U N T</div>
          <h3 style={styles.cardTitle}>Account Settings</h3>
        </div>

        <div style={styles.infoList}>
          <div className="bp-info-item" style={styles.infoItem}>
            <div style={styles.infoLeft}>
              <div style={styles.infoIcon}>
                <User style={{ width: '16px', height: '16px', color: '#71717a' }} />
              </div>
              <div>
                <div style={styles.infoLabel}>Brand Name</div>
                <div style={styles.infoValue}>{profile?.name}</div>
              </div>
            </div>
            <a 
              href={`mailto:yemberzal.me@gmail.com?subject=Request to Change Brand Name&body=Hello,%0D%0A%0D%0AI would like to request a change to my brand name.%0D%0A%0D%0ACurrent Brand Name: ${encodeURIComponent(profile?.name || '')}%0D%0ARequested Change:%0D%0A%0D%0AThank you!`}
              style={styles.supportLink}
            >
              Contact Support
            </a>
          </div>

          <div className="bp-info-item" style={styles.infoItem}>
            <div style={styles.infoLeft}>
              <div style={styles.infoIcon}>
                <Mail style={{ width: '16px', height: '16px', color: '#71717a' }} />
              </div>
              <div>
                <div style={styles.infoLabel}>Email Address</div>
                <div style={styles.infoValue}>{profile?.email}</div>
              </div>
            </div>
            <a 
              href={`mailto:yemberzal.me@gmail.com?subject=Request to Change Email Address&body=Hello,%0D%0A%0D%0AI would like to request a change to my email address.%0D%0A%0D%0ACurrent Email: ${encodeURIComponent(profile?.email || '')}%0D%0ARequested Change:%0D%0A%0D%0AThank you!`}
              style={styles.supportLink}
            >
              Contact Support
            </a>
          </div>

          {profile?.website && (
            <div style={{ ...styles.infoItem, borderBottom: 'none' }}>
              <div style={styles.infoLeft}>
                <div style={styles.infoIcon}>
                  <Globe style={{ width: '16px', height: '16px', color: '#71717a' }} />
                </div>
                <div>
                  <div style={styles.infoLabel}>Website</div>
                  <div style={styles.infoValue}>{profile.website}</div>
                </div>
              </div>
              <a 
                href={profile.website} 
                target="_blank" 
                rel="noopener noreferrer" 
                style={styles.visitLink}
              >
                Visit →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '42px',
    fontWeight: '400',
    color: '#171717',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: 0,
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    color: '#171717',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  errorAlert: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '16px 20px',
    borderRadius: '12px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  successAlert: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#16a34a',
    padding: '16px 20px',
    borderRadius: '12px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  alertClose: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#ef4444',
    padding: '4px',
  },
  alertCloseSuccess: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#16a34a',
    padding: '4px',
  },
  profileCard: {
    background: '#fafafa',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  avatarContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  avatar: {
    width: '96px',
    height: '96px',
    borderRadius: '16px',
    objectFit: 'cover',
    border: '2px solid #e5e5e5',
  },
  avatarPlaceholder: {
    width: '96px',
    height: '96px',
    borderRadius: '16px',
    background: '#171717',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: '600',
  },
  avatarOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#171717',
    margin: '0 0 4px 0',
  },
  profileEmail: {
    fontSize: '14px',
    color: '#71717a',
    margin: '0 0 12px 0',
  },
  profileMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  statusBadge: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '6px',
    border: '1px solid',
  },
  metaText: {
    fontSize: '13px',
    color: '#71717a',
  },
  metaDot: {
    color: '#d4d4d4',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  cardHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#71717a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#171717',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    background: '#fafafa',
    color: '#71717a',
    cursor: 'not-allowed',
  },
  inputWithPrefix: {
    position: 'relative',
  },
  inputPrefix: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#a1a1aa',
    fontSize: '15px',
  },
  inputWithPrefixField: {
    paddingLeft: '36px',
  },
  select: {
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2371717a' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
  },
  textarea: {
    resize: 'vertical',
    minHeight: '120px',
    lineHeight: '1.6',
  },
  charCount: {
    fontSize: '12px',
    color: '#a1a1aa',
    textAlign: 'right',
  },
  uploadContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    color: '#171717',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  uploadHint: {
    fontSize: '12px',
    color: '#a1a1aa',
  },
  logoPreview: {
    marginTop: '12px',
  },
  logoPreviewImg: {
    width: '64px',
    height: '64px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: '1px solid #e5e5e5',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #f0f0f0',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    color: '#171717',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#171717',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  infoLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  infoIcon: {
    width: '40px',
    height: '40px',
    background: '#f5f5f5',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    fontSize: '13px',
    color: '#71717a',
    marginBottom: '2px',
  },
  infoValue: {
    fontSize: '15px',
    color: '#171717',
    fontWeight: '500',
  },
  infoNote: {
    fontSize: '12px',
    color: '#a1a1aa',
  },
  visitLink: {
    fontSize: '14px',
    color: '#171717',
    fontWeight: '500',
    textDecoration: 'none',
  },
  supportLink: {
    fontSize: '13px',
    color: '#171717',
    fontWeight: '500',
    textDecoration: 'none',
    padding: '6px 12px',
    background: '#f5f5f5',
    borderRadius: '6px',
    transition: 'background 0.15s',
  },
};

export default BrandProfile;
